import { createContext, useContext, useMemo } from "react";
import type { Interaction } from "../types";
import type { EntityInfo } from "./useEntityMap";
import { activityStart, type SegmentDayLookup } from "../utils/tripDisplay";
import { groupHotelStays, stayToReturnTo, visitPositions, type HotelStay } from "../utils/hotelStays";

type Activity = Interaction & { id: string };

/**
 * A trip's hotel stays, worked out from its activities (see
 * `utils/hotelStays`), plus the stay you'd come back to right now.
 */
export function useHotelStays(
  interactions: Activity[],
  entityDataMap: Record<string, EntityInfo | undefined>,
  onDay?: SegmentDayLookup,
) {
  return useMemo(() => {
    const stays = groupHotelStays(
      interactions,
      (id) => entityDataMap[id]?.type === "HOTEL",
      { startOf: (v) => activityStart(v, onDay) },
    );
    return {
      stays,
      returnTo: stayToReturnTo(stays),
      positions: visitPositions(stays as HotelStay[]),
    };
  }, [interactions, entityDataMap, onDay]);
}

/** Which visit of its stay each hotel activity is — read by the itinerary rows. */
export const StayPositionsContext = createContext<ReturnType<typeof visitPositions> | null>(null);

export function useStayPosition(interactionId: string) {
  return useContext(StayPositionsContext)?.get(interactionId) ?? null;
}
